import {
    useStyles,
    Flex,
    Divider,
    useTheme,
    Grid,
} from '@archipro-design/aria';
import { useLoaderData } from '@remix-run/react';
import { useRef } from 'react';
import { Container } from '@modules/professional/component/page-layout/PageLayout.style';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import * as S from '@modules/professional/component/contact-tab/ContactPage.desktop.style';
import { FindNearest } from '@modules/professional/component/contact-tab/FindNearest';

import { LocationList } from '@modules/professional/component/contact-tab/LocationList.desktop';
import { LocationMap } from '@modules/professional/component/contact-tab/LocationMap';
import { SearchBar } from '@modules/professional/component/contact-tab/SearchBar';

import { getLinkedProfileLocations } from '@modules/professional/util/contact-page-helper';
import type {
    ContactLoaderData,
    LocationDataType,
} from '@modules/professional/type/contact-page-types';
import { useContactLocationMap } from '@modules/professional/hook/use-contact-location-map';
import { useTracker } from '@archipro-website/tracker';
import { useIsDisabledInPreview } from '@modules/root/hook/use-is-disabled-in-preview';
import { useClickToReveal } from '~/modules/root/hook/use-click-to-reveal';

const ContactPageDesktop = () => {
    const { css } = useStyles();
    const { siteVariables } = useTheme();
    const tracker = useTracker();
    const initialRouteData = useProfessionalInitialRouteData();
    const loaderData = useLoaderData<ContactLoaderData>();
    const linkedProfiles =
        loaderData?.linkedProfiles as ContactLoaderData['linkedProfiles'];

    const originalLocations = getLinkedProfileLocations(
        initialRouteData,
        linkedProfiles ?? {
            linkedProfilesCount: 0,
            linkedProfilesList: [],
            linkedProfilesProductSharing: [],
        }
    );
    const { onRevealClick } = useClickToReveal({
        revealType: 'Location',
        revealKey: `professional-${initialRouteData.ID}`,
    });

    const leftListSectionRef = useRef<HTMLDivElement>(null);

    const disableContactEnquiry = useIsDisabledInPreview('contact_enquire');
    const disableContactViewProfile = useIsDisabledInPreview(
        'contact_view_profile'
    );

    const {
        onSearchChange,
        onSearchClick,
        onMapMarkerHover,
        onMapMarkerClick,
        onLocationDetailClose,
        onListItemClick,
        onListItemHover,
        calculateDistance,
        mapCenter,
        mapZoomNum,
        clickedLocation,
        hoveredLocation,
        filteredLocations,
        sortedLocations,
        hasDistance,
    } = useContactLocationMap(
        originalLocations,
        leftListSectionRef,
        true,
        initialRouteData.Country
    );

    return (
        <div className={css(Container)}>
            <Divider
                size={1}
                variables={{
                    dividerPadding: 0,
                    dividerPrimaryColor: siteVariables.colors.primitive.black,
                }}
            />
            {sortedLocations.length > 0 && (
                <Flex space="between" className={css(S.ContactContainer)}>
                    <div
                        className={css(S.LeftListSection)}
                        ref={leftListSectionRef}
                    >
                        {sortedLocations.length > 1 && (
                            <Grid
                                columns={2}
                                className={css(S.LeftListActions)}
                            >
                                <div>
                                    <SearchBar
                                        hideInput={sortedLocations.length < 2}
                                        onSearchChange={(keyword: string) => {
                                            tracker.log(
                                                'ProfessionalLocationSearch',
                                                {
                                                    url: new URL(
                                                        window.location.href
                                                    ),
                                                    targetTracker:
                                                        'archiproTracker',
                                                    data: {
                                                        ExtraData: keyword,
                                                    },
                                                }
                                            );
                                            onSearchChange?.(keyword);
                                        }}
                                        onSearchClick={(keyword: string) => {
                                            tracker.log(
                                                'ProfessionalLocationSearchClick',
                                                {
                                                    url: new URL(
                                                        window.location.href
                                                    ),
                                                    targetTracker:
                                                        'archiproTracker',
                                                }
                                            );
                                            onSearchClick?.(keyword);
                                        }}
                                    />
                                </div>
                                <div>
                                    {!hasDistance && (
                                        <FindNearest
                                            calculateDistance={
                                                calculateDistance
                                            }
                                        />
                                    )}
                                </div>
                            </Grid>
                        )}
                        <Divider
                            size={2}
                            variables={{
                                dividerPadding: 0,
                                dividerPrimaryColor:
                                    siteVariables.colors.grey[125],
                            }}
                        />
                        <LocationList
                            locations={filteredLocations}
                            professional={initialRouteData}
                            onListItemClick={(location: LocationDataType) => {
                                onRevealClick();
                                onListItemClick(location);
                            }}
                            onListItemHover={onListItemHover}
                            onLocationDetailClose={onLocationDetailClose}
                            hoveredLocation={hoveredLocation}
                            clickedLocation={clickedLocation}
                            originLocationCount={sortedLocations.length}
                            disableEnquire={disableContactEnquiry}
                            disableContactViewProfile={
                                disableContactViewProfile
                            }
                        />
                    </div>
                    <div className={css(S.RightMapSection)}>
                        <LocationMap
                            center={mapCenter}
                            zoom={mapZoomNum}
                            filteredLocations={filteredLocations}
                            onMapMarkerHover={onMapMarkerHover}
                            onMapMarkerClick={(location: LocationDataType) => {
                                tracker.log('ProfessionalMapPinClick', {
                                    url: new URL(window.location.href),
                                    targetTracker: 'archiproTracker',
                                });
                                onMapMarkerClick?.(location);
                            }}
                            hoveredLocation={hoveredLocation}
                            clickedLocation={clickedLocation}
                            linkedProfiles={linkedProfiles}
                        />
                    </div>
                </Flex>
            )}
        </div>
    );
};

export default ContactPageDesktop;
